import { extractExamDateKey } from '../utils/exam-date';
import { ApiError, apiJson } from './client';

export interface StudentMe {
  studentId: string;
  firstName: string;
  ageBracket: 'under_18' | 'adult';
  examDate: string | null;
  schoolName: string | null;
  updatedAt: string;
}

export interface PatchStudentMeRequest {
  expectedUpdatedAt: string;
  firstName?: string;
  examDate?: string | null;
}

export type PatchStudentMeResult =
  | { status: 'updated'; profile: StudentMe }
  | { status: 'conflict'; current: StudentMe };

type UnknownRecord = Record<string, unknown>;
const CANONICAL_UTC_MILLISECOND_PATTERN = /^(\d{4})-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: UnknownRecord, expected: readonly string[]): boolean {
  const keys = Object.keys(value);
  return keys.length === expected.length && expected.every((key) => keys.includes(key));
}

function isCanonicalUtcMillisecondTimestamp(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const match = CANONICAL_UTC_MILLISECOND_PATTERN.exec(value);
  if (!match || Number(match[1]) < 1) return false;
  const parsed = new Date(value);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString() === value;
}

function isStudentMe(value: unknown): value is StudentMe {
  if (!isRecord(value) || !hasExactKeys(value, [
    'studentId',
    'firstName',
    'ageBracket',
    'examDate',
    'schoolName',
    'updatedAt',
  ])) {
    return false;
  }

  return typeof value.studentId === 'string'
    && typeof value.firstName === 'string'
    && (value.ageBracket === 'under_18' || value.ageBracket === 'adult')
    && (value.examDate === null
      || (typeof value.examDate === 'string' && extractExamDateKey(value.examDate) === value.examDate))
    && (value.schoolName === null || typeof value.schoolName === 'string')
    && isCanonicalUtcMillisecondTimestamp(value.updatedAt);
}

function isProfileConflictEnvelope(
  value: unknown,
): value is { error: 'profile_conflict'; current: StudentMe } {
  return isRecord(value)
    && hasExactKeys(value, ['error', 'current'])
    && value.error === 'profile_conflict'
    && isStudentMe(value.current);
}

export const studentProfileApi = {
  me(signal?: AbortSignal): Promise<StudentMe> {
    return apiJson<StudentMe>('/api/me', { method: 'GET', signal });
  },

  async update(
    request: PatchStudentMeRequest,
    signal?: AbortSignal,
  ): Promise<PatchStudentMeResult> {
    try {
      const profile = await apiJson<StudentMe>('/api/me', {
        method: 'PATCH',
        body: JSON.stringify(request),
        signal,
      });
      return { status: 'updated', profile };
    } catch (error) {
      if (
        error instanceof ApiError
        && error.status === 409
        && isProfileConflictEnvelope(error.body)
      ) {
        return { status: 'conflict', current: error.body.current };
      }
      throw error;
    }
  },
};
